import dayjs from "dayjs";

import { HelpVideo } from "./types";

export const MAX_CARD_VISITS = 3;
/** A video counts as "new" for this many days from its releasedOn date. */
export const NEW_FOR_DAYS = 45;

export interface HelpState {
  /** Last position reached, in seconds, per video revision (see progressKey). */
  progress: Record<string, number>;
  dismissed: Record<string, boolean>;
  /** Visits on which the New card was shown. */
  cardShown: Record<string, number>;
  /** "YYYY-MM-DD" on which "Later" was pressed. */
  laterOn: Record<string, string>;
  /** Language code picked in the player; the default for every video that has it. */
  language?: string;
}

export const EMPTY_HELP_STATE: HelpState = {
  progress: {},
  dismissed: {},
  cardShown: {},
  laterOn: {},
};

const DAY_FORMAT = "YYYY-MM-DD";

export const todayKey = (): string => dayjs().format(DAY_FORMAT);

const storageKey = (userId: string | number): string => `casa.help.v1.${userId}`;

export const loadHelpState = (userId: string | number): HelpState => {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    return raw ? { ...EMPTY_HELP_STATE, ...JSON.parse(raw) } : EMPTY_HELP_STATE;
  } catch {
    return EMPTY_HELP_STATE;
  }
};

export const saveHelpState = (userId: string | number, state: HelpState): void => {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(state));
  } catch {
    // Storage may be full or blocked; help state is best effort.
  }
};

/** New from releasedOn until NEW_FOR_DAYS days later. `today` is "YYYY-MM-DD". */
export const isNew = (video: HelpVideo, today: string): boolean => {
  const age = dayjs(today).diff(dayjs(video.releasedOn), "day");
  return age >= 0 && age < NEW_FOR_DAYS;
};

/** Progress is kept per file revision, so a re-recorded video starts fresh. */
export const progressKey = (video: Pick<HelpVideo, "id" | "rev">): string => `${video.id}@${video.rev}`;

export const progressOf = (video: HelpVideo, state: HelpState): number => state.progress[progressKey(video)] ?? 0;

/** Watched once playback reached the last timeline entry (the end card). */
export const isWatched = (video: HelpVideo, state: HelpState): boolean => {
  const last = video.chapters[video.chapters.length - 1];
  return !!last && progressOf(video, state) >= last.start;
};

/** New, and the user has neither watched nor dismissed it. */
export const isUnseenNew = (
  video: HelpVideo,
  state: HelpState,
  today: string
): boolean =>
  isNew(video, today) && !state.dismissed[video.id] && !isWatched(video, state);

/** The on-page "New" card: unseen, shown on fewer than 3 visits, not postponed today. */
export const cardVisible = (
  video: HelpVideo,
  state: HelpState,
  today: string
): boolean =>
  isUnseenNew(video, state, today) &&
  (state.cardShown[video.id] ?? 0) < MAX_CARD_VISITS &&
  state.laterOn[video.id] !== today;
